import {
  buildApplePayUrl,
  createStateMachine,
  expectedOriginOf,
  translateApplePayEvent,
} from '@transak/ui-components-core'
import type {
  ApplePayCallbacks,
  ApplePayEnvironment,
} from '@transak/ui-components-core'
import { useEffect, useMemo, useRef } from 'react'
import { WebView, type WebViewMessageEvent } from 'react-native-webview'

export type TransakApplePaySurfaceProps = ApplePayCallbacks & {
  sessionId: string
  environment: ApplePayEnvironment
}

function parseMessage(data: string): unknown {
  try {
    return JSON.parse(data)
  } catch {
    return null
  }
}

// Throws on anything it will not vouch for, including a non-https scheme.
function originOf(url: string): string | undefined {
  try {
    return expectedOriginOf(url)
  } catch {
    return undefined
  }
}

export function TransakApplePaySurface({
  sessionId,
  environment,
  onReady,
  onSuccess,
  onError,
}: TransakApplePaySurfaceProps) {
  const callbacks = useRef({ onReady, onSuccess, onError })
  callbacks.current = { onReady, onSuccess, onError }

  const uri = useMemo(
    () => buildApplePayUrl({ sessionId, environment }),
    [environment, sessionId],
  )
  const machine = useMemo(
    () =>
      createStateMachine(() => ({
        onReady: () => callbacks.current.onReady?.(),
        onSuccess: (payload) => callbacks.current.onSuccess?.(payload),
        onError: (error) => callbacks.current.onError?.(error),
      })),
    [environment, sessionId],
  )

  useEffect(() => {
    machine.apply({ type: 'mount' })
    return () => machine.apply({ type: 'unmount' })
  }, [machine])

  const expectedOrigin = useMemo(() => originOf(uri), [uri])

  // `originWhitelist` only prefix-matches, so "<origin>.evil.test" clears it.
  // Top-frame navigation is pinned to the exact origin here; subframes stay on
  // the whitelist, since the provider draws the sheet in one.
  const shouldStartLoad = (request: {
    url: string
    isTopFrame?: boolean
  }): boolean =>
    request.isTopFrame !== true || originOf(request.url) === expectedOrigin

  const handleMessage = (event: WebViewMessageEvent) => {
    // Only the provider document may drive the machine. A page this WebView
    // redirected to could otherwise post a success with a requestId of its
    // choosing, which we would report as a real capture.
    if (
      expectedOrigin === undefined ||
      originOf(event.nativeEvent.url) !== expectedOrigin
    ) {
      return
    }
    const transition = translateApplePayEvent(
      parseMessage(event.nativeEvent.data),
    )
    if (transition !== null) {
      machine.apply(transition)
    }
  }

  // A page that never loads emits no events, so the slot uncovers an inert
  // widget at its ready ceiling. Erroring the machine routes it to the same
  // failure the screen already offers a retry from.
  const failLoad = (code: string) =>
    machine.apply({ type: 'error', payload: { code } })

  return (
    <WebView
      source={{ uri }}
      onMessage={handleMessage}
      onError={({ nativeEvent }) => failLoad(`load_failed_${nativeEvent.code}`)}
      onHttpError={({ nativeEvent }) => {
        // Android reports subresource failures here too; a dead beacon must
        // not take the sheet down with it.
        if (nativeEvent.url === uri) {
          failLoad(`http_${nativeEvent.statusCode}`)
        }
      }}
      originWhitelist={expectedOrigin === undefined ? [] : [expectedOrigin]}
      onShouldStartLoadWithRequest={shouldStartLoad}
      enableApplePay
      style={{ flex: 1, backgroundColor: 'transparent' }}
    />
  )
}
